

export default class DielineToSvgConverter {
    
    constructor(layers, width, height) {
        this.SVG = this.convertToSVG(layers, width, height);
    }

    convertToSVG(dielineData, targetWidthPx, targetHeightPx) {
        // Define a canvas/viewBox padding to ensure all edges are visible
        const padding = 20;

        // Calculate bounding box to dynamically size the SVG's internal viewBox
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        dielineData.forEach(layer => {
            layer.objects.forEach(obj => {
                if (obj.points) {
                    obj.points.forEach(p => {
                        if (p.x < minX) minX = p.x;
                        if (p.y < minY) minY = p.y;
                        if (p.x > maxX) maxX = p.x;
                        if (p.y > maxY) maxY = p.y;
                    });
                }
            });
        });

        // Calculate the internal coordinate dimensions
        const originX = Math.min(0, minX) - padding;
        const originY = Math.min(0, minY) - padding;
        const boxWidth = Math.ceil(maxX - Math.min(0, minX)) + padding * 2;
        const boxHeight = Math.ceil(maxY - Math.min(0, minY)) + padding * 2;

        const viewBox = `${originX} ${originY} ${boxWidth} ${boxHeight}`;

        // Apply target dimensions or fallback to the internal box dimensions
        const finalWidth = targetWidthPx ? `${targetWidthPx}px` : boxWidth;
        const finalHeight = targetHeightPx ? `${targetHeightPx}px` : boxHeight;

        // Start building the SVG string. preserveAspectRatio ensures uniform scaling.
        let svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="${finalWidth}" height="${finalHeight}" preserveAspectRatio="xMidYMid meet">\n`;
        //svgString += `  <style>\n    text { font-family: sans-serif; }\n  </style>\n`;

        // Helper to parse pen and brush into SVG attributes
        const getStyleAttr = (pen, brush) => {
            let style = "";

            // Stroke styles
            if (pen) {
                // Enforce a strict 1px stroke width with your non-scaling vector effect
                style += `stroke="${pen.color}" stroke-width="1" vector-effect="non-scaling-stroke" `;

                if (pen.dashStyle === "Custom") {
                    // Renders a precise circular dotted line pattern
                    style += `stroke-dasharray="0 4" stroke-linecap="round" `;
                } else if (pen.dashStyle && pen.dashStyle !== "Solid") {
                    style += `stroke-dasharray="5 5" `;
                }
            } else {
                style += `stroke="none" `;
            }

            // Fill styles
            if (brush) {
                style += `fill="${brush.color}" fill-opacity="${brush.opacity}" `;
            } else {
                style += `fill="none" `;
            }

            return style;
        };

        // Iterate over each layer
        dielineData.forEach(layer => {
            svgString += `  <g id="${layer.name.replace(/\s+/g, '_')}">\n`;

            layer.objects.forEach(obj => {
                const styleAttr = getStyleAttr(obj.pen, obj.brush);
                const pts = obj.points;

                switch (obj.type) {
                    case "Rectangle":
                        if (pts && pts.length === 2) {
                            const rx = Math.min(pts[0].x, pts[1].x);
                            const ry = Math.min(pts[0].y, pts[1].y);
                            const rw = Math.abs(pts[1].x - pts[0].x);
                            const rh = Math.abs(pts[1].y - pts[0].y);
                            svgString += `    <rect x="${rx}" y="${ry}" width="${rw}" height="${rh}" ${styleAttr}/>\n`;
                        }
                        break;

                    case "Line":
                        if (pts && pts.length === 2) {
                            svgString += `    <line x1="${pts[0].x}" y1="${pts[0].y}" x2="${pts[1].x}" y2="${pts[1].y}" ${styleAttr}/>\n`;
                        }
                        break;

                    case "Polygon":
                        if (pts && pts.length > 2) {
                            const pointsStr = pts.map(p => `${p.x},${p.y}`).join(" ");
                            svgString += `    <polygon points="${pointsStr}" ${styleAttr}/>\n`;
                        }
                        break;

                    case "Text":
                        if (pts && pts.length >= 1) {
                            const tx = pts[0].x;
                            const ty = pts[0].y;
                            const transform = obj.angle ? `transform="rotate(${obj.angle} ${tx} ${ty})"` : "";
                            svgString += `    <text x="${tx}" y="${ty}" font-size="${obj.fontSize}" ${styleAttr} ${transform}>${obj.text}</text>\n`;
                        }
                        break;
                    case "Arc":
                        if (pts && pts.length === 2) {
                            // 1. Calculate bounding box dimensions
                            const x1 = pts[0].x;
                            const y1 = pts[0].y;
                            const x2 = pts[1].x;
                            const y2 = pts[1].y;

                            const rx = Math.abs(x2 - x1) / 2;
                            const ry = Math.abs(y2 - y1) / 2;
                            const cx = Math.min(x1, x2) + rx;
                            const cy = Math.min(y1, y2) + ry;

                            // 2. Parse angles (convert degrees to radians)
                            const startAngleDeg = obj.startAngle || 0;
                            const sweepAngleDeg = obj.sweepAngle || 0;
                            const endAngleDeg = startAngleDeg + sweepAngleDeg;

                            const startRad = (startAngleDeg * Math.PI) / 180;
                            const endRad = (endAngleDeg * Math.PI) / 180;

                            // 3. Calculate exact start and end points on the ellipse perimeter
                            const startX = cx + rx * Math.cos(startRad);
                            const startY = cy + ry * Math.sin(startRad);
                            const endX = cx + rx * Math.cos(endRad);
                            const endY = cy + ry * Math.sin(endRad);

                            // 4. Determine SVG Arc flags
                            // Large arc flag is 1 if the absolute sweep angle is greater than 180 degrees
                            const largeArcFlag = Math.abs(sweepAngleDeg) > 180 ? 1 : 0;
                            // Sweep flag determines direction (1 for clockwise/positive, 0 for counter-clockwise/negative)
                            const sweepFlag = sweepAngleDeg >= 0 ? 1 : 0;

                            // 5. Build the SVG Path string
                            svgString += `    <path d="M ${startX} ${startY} A ${rx} ${ry} 0 ${largeArcFlag} ${sweepFlag} ${endX} ${endY}" ${styleAttr}/>\n`;
                        }
                        break;
                    case "Ellipse":
                        if (pts && pts.length === 2) {
                            const x1 = pts[0].x;
                            const y1 = pts[0].y;
                            const x2 = pts[1].x;
                            const y2 = pts[1].y;

                            // Calculate the horizontal and vertical radii (half the bounding box width/height)
                            const rx = Math.abs(x2 - x1) / 2;
                            const ry = Math.abs(y2 - y1) / 2;

                            // Calculate the center point of the ellipse
                            const cx = Math.min(x1, x2) + rx;
                            const cy = Math.min(y1, y2) + ry;

                            svgString += `    <ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" ${styleAttr}/>\n`;
                        }
                        break;

                    default:
                        console.log(`Unsupported object type: ${obj.type}`);
                }
            });

            svgString += `  </g>\n`;
        });

        svgString += `</svg>`;
        return svgString;
    }

}